import { useState, useEffect, useRef } from "react"
import Command from "@/v2-ui/command"
import { useDebouncedValue } from "@/v2-ui/utils/utils.debounce"
import { useSelectorContext, type SearchStatus } from "./selector.context"

export type SelectorSearchProps = {
  placeholder?: string
  onSearch?(value: string): void
  debounce?: number
  minLength?: number
  /** Pre-seeds the search box (and thus the first query) — e.g. a picker
   *  opened from a form field that already knows the likely name. */
  defaultValue?: string
}

function deriveSearchStatus(length: number, minLength: number): SearchStatus {
  if (length === 0) return "idle"
  if (length < minLength) return "below-min"
  return "ready"
}

function SelectorSearch(props: SelectorSearchProps) {
  const {
    placeholder = "Search...",
    onSearch,
    debounce = 300,
    minLength = 2,
    defaultValue,
  } = props

  const { setSearchStatus, minSearchLengthRef } = useSelectorContext()

  const [value, setValue] = useState(defaultValue ?? "")
  const debouncedValue = useDebouncedValue(value, debounce)
  const prevStatusRef = useRef<SearchStatus>("idle")

  useEffect(() => {
    minSearchLengthRef.current = minLength
  }, [minLength])

  useEffect(() => {
    onSearch?.(debouncedValue)
  }, [debouncedValue])

  useEffect(() => {
    const status = deriveSearchStatus(value.length, minLength)
    if (prevStatusRef.current !== status) {
      prevStatusRef.current = status
      setSearchStatus(status)
    }
  }, [value, minLength])

  return (
    <Command.Input
      placeholder={placeholder}
      value={value}
      onValueChange={setValue}
      autoFocus
    />
  )
}

export default SelectorSearch
